import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { Injectable } from "@angular/core";
import { DateService } from "./date.service";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";

@Injectable({
  providedIn: "root",
})
export class ValidatorService extends BaseService {
  constructor(
    private dateService: DateService,
    logService: LogService,
  ) {
    super(logService, "ValidatorService");
  }

  public isFieldRequired(formField: any) {
    const required = formField.required || formField.properties.required;
    return required && required.toString().toLowerCase() === "true";
  }

  public isConditionallyRequired(formField: any) {
    return formField.required && formField.required.toString().includes(":");
  }

  public getFieldAccessor(fieldAccessors: any, formFieldID: string) {
    return fieldAccessors[formFieldID];
  }

  public createCrossFieldValidator(
    condition: any,
    target: string,
    conditional: string,
    errorKey: string,
    errorDetails: any = "error",
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const targetControl = group.get(target);
      const conditionalControl = group.get(conditional);
      if (condition(targetControl, conditionalControl)) {
        targetControl?.setErrors({ [errorKey]: errorDetails });
      } else {
        targetControl?.setErrors(null);
      }
      return group.errors;
    };
  }

  public getFieldValidators(formField: any) {
    const validators = [];
    if (this.isFieldRequired(formField)) {
      validators.push(Validators.required);
    }
    if (formField.properties?.maxlength || formField?.dbMaxLength) {
      const dbMaxLength = Number.parseInt(formField?.dbMaxLength, 10);
      const maxlength = Number.parseInt(formField.properties?.maxlength, 10);
      if (!Number.isNaN(maxlength) || !Number.isNaN(dbMaxLength)) {
        const lesserMaxLength = Math.min(
          Number.isNaN(dbMaxLength) ? Number.MAX_SAFE_INTEGER : dbMaxLength,
          Number.isNaN(maxlength) ? Number.MAX_SAFE_INTEGER : maxlength,
        );
        validators.push(Validators.maxLength(lesserMaxLength));
      }
    }
    if (formField.properties?.min) {
      const min = Number.parseInt(formField.properties?.min, 10);
      if (!Number.isNaN(min)) {
        validators.push(Validators.min(min));
      }
    }
    if (formField.properties?.max) {
      const max = Number.parseInt(formField.properties?.max, 10);
      if (!Number.isNaN(max)) {
        validators.push(Validators.max(max));
      }
    }
    if (formField.properties?.pattern) {
      validators.push(Validators.pattern(formField.properties?.pattern));
    }
    return validators;
  }

  public getFormValidators(
    formField: any,
    fieldAccessors?: any,
    fieldDefinitionMap?: Record<string, any>,
  ) {
    const validators = [];
    if (this.isConditionallyRequired(formField)) {
      const [fieldToCheck, valueToCheck] = formField.required.split(":");
      const fieldAccessor = this.getFieldAccessor(fieldAccessors, fieldToCheck);
      const conditionalFieldId = fieldAccessor?.id || fieldToCheck;
      const condition = (target: FormControl, conditional: FormControl) => {
        const conditionalCheck = fieldAccessor?.accessor
          ? fieldAccessor.accessor(conditional?.value) === valueToCheck
          : conditional?.value === valueToCheck;
        return conditionalCheck && !target.value;
      };
      validators.push(
        this.createCrossFieldValidator(
          condition,
          formField.properties.id,
          conditionalFieldId,
          "required",
          true,
        ),
      );
    }

    const entryValidationLogic = Array.isArray(formField.logic)
      ? formField.logic.filter(
          (logic: any) =>
            logic?.type === "entryValidation" && logic?.validationType,
        )
      : [];
    validators.push(
      ...entryValidationLogic.map((logic: any) =>
        this.createEntryValidationValidator(
          formField,
          logic,
          fieldDefinitionMap,
        ),
      ),
    );
    return validators;
  }

  private createEntryValidationValidator(
    formField: any,
    logic: any,
    fieldDefinitionMap?: Record<string, any>,
  ): ValidatorFn {
    const fieldId = formField?.properties?.id;
    const typeMap: Record<string, string> = {
      mustBeGreater: ">",
      mustBeLess: "<",
      mustBeEqual: "===",
      mustNotBeEqual: "!==",
    };

    return (group: AbstractControl): ValidationErrors | null => {
      const targetControl = group.get(fieldId || "");
      if (!targetControl) {
        return null;
      }

      const entryValue = targetControl.value;
      const shouldValidate =
        entryValue !== undefined && entryValue !== null && `${entryValue}`; // string type coercion for falsy values like 0

      const updateEntryError = (
        hasError: boolean,
        errorDetails: any = null,
        errorKey: string = "entryValidation",
      ) => {
        const existingErrors = targetControl.errors || {};
        if (hasError) {
          targetControl.setErrors({
            ...existingErrors,
            [errorKey]: errorDetails,
          });
        } else if (existingErrors[errorKey]) {
          const { [errorKey]: removed, ...rest } = existingErrors;
          targetControl.setErrors(Object.keys(rest).length ? rest : null);
        }
      };

      if (!shouldValidate) {
        updateEntryError(false);
        return null;
      }

      if (logic.validationType === "preventFutureDates") {
        const parsedDate = this.dateService.parse(entryValue);
        if (
          this.dateService.isValid(parsedDate) &&
          this.dateService.isAfter(parsedDate, new Date())
        ) {
          updateEntryError(true, {
            message: "You cannot enter a future date",
            type: logic.validationType,
          });
          return null;
        }
        updateEntryError(false);
        return null;
      }

      const rules = Array.isArray(logic.rules) ? logic.rules : [];
      const ruleError = rules
        .map((rule: any) =>
          this.getEntryValidationRuleError(
            rule,
            logic,
            entryValue,
            formField,
            fieldDefinitionMap,
            typeMap,
            group,
          ),
        )
        .find(Boolean);

      if (ruleError) {
        updateEntryError(true, ruleError.details, ruleError.key);
        return null;
      }
      updateEntryError(false);
      return null;
    };
  }

  private parseEntryComparableValue(value: any, fieldType?: string) {
    if (value === undefined || value === null) {
      return { comparable: null };
    }
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed === "") {
      return { comparable: null };
    }
    const normalizedType = fieldType?.toLowerCase?.() || "";
    if (normalizedType.includes("date") || normalizedType.includes("time")) {
      const parsedDate = this.dateService.parse(trimmed);
      if (this.dateService.isValid(parsedDate)) {
        return { comparable: parsedDate.getTime() };
      }
      return { comparable: null };
    }
    const numericValue =
      typeof trimmed === "number" ? trimmed : Number.parseFloat(trimmed);
    if (!Number.isNaN(numericValue)) {
      return { comparable: numericValue };
    }
    return { comparable: trimmed };
  }

  private evaluateEntryComparison(
    operation: string,
    entryValue: any,
    compareValue: any,
  ): boolean {
    switch (operation) {
      case "mustBeGreater":
        return entryValue > compareValue;
      case "mustBeLess":
        return entryValue < compareValue;
      case "mustBeEqual":
        return entryValue === compareValue;
      case "mustNotBeEqual":
        return entryValue !== compareValue;
      default:
        return true;
    }
  }

  private getEntryValidationMessage(
    validationType: string,
    fieldLabel: string,
    compareValue: any,
  ): string {
    const displayValue = compareValue ?? "";
    switch (validationType) {
      case "mustBeGreater":
        return `${fieldLabel} must be greater than ${displayValue}`;
      case "mustBeLess":
        return `${fieldLabel} must be smaller than ${displayValue}`;
      case "mustBeEqual":
        return `${fieldLabel} must equal to ${displayValue}`;
      case "mustNotBeEqual":
        return `${fieldLabel} must not equal to ${displayValue}`;
      default:
        return `${fieldLabel} is invalid`;
    }
  }

  private getEntryValidationRuleError(
    rule: any,
    logic: any,
    entryValue: any,
    formField: any,
    fieldDefinitionMap: Record<string, any> | undefined,
    typeMap: Record<string, string>,
    group: AbstractControl,
  ): { key: string; details: any } | null {
    const expectedAction = typeMap[logic.validationType];
    if (expectedAction && rule.valueAction !== expectedAction) {
      this.logger.warn(
        `Mismatched valueAction "${rule.valueAction}" for validationType "${logic.validationType}"`,
      );
      return null;
    }

    const compareFieldId = rule.comparedWithDomId || rule.idOverride;
    const compareControl = compareFieldId ? group.get(compareFieldId) : null;
    const compareRawValue = rule.isValueTypeInput
      ? rule.value
      : compareControl?.value;

    if (
      compareRawValue === undefined ||
      compareRawValue === null ||
      `${compareRawValue}` === ""
    ) {
      return null;
    }

    const entryType = formField?.type;
    const compareType = compareFieldId
      ? fieldDefinitionMap?.[compareFieldId]?.type
      : undefined;

    const entryComparable = this.parseEntryComparableValue(
      entryValue,
      entryType,
    );
    const compareComparable = this.parseEntryComparableValue(
      compareRawValue,
      compareType,
    );

    if (
      entryComparable.comparable === null ||
      compareComparable.comparable === null
    ) {
      return null;
    }

    const errorMessage = this.getEntryValidationMessage(
      logic.validationType,
      formField?.properties?.label || "This field",
      compareRawValue,
    );

    const isValid = this.evaluateEntryComparison(
      logic.validationType,
      entryComparable.comparable,
      compareComparable.comparable,
    );

    if (isValid) {
      return null;
    }

    return {
      key: "entryValidation",
      details: {
        message: errorMessage,
        type: logic.validationType,
      },
    };
  }

  public createSyncValidator(
    condition: any,
    errorKey: string,
    errorDetails: any = "error",
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (condition(control)) {
        return { [errorKey]: errorDetails };
      }
      return null;
    };
  }

  public createAsyncValidator(
    condition: any,
    errorKey: string,
    errorDetails: any = "error",
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (condition(control)) {
        return of({ [errorKey]: errorDetails });
      }
      return of(null);
    };
  }

  public setValidators(control: AbstractControl, validators: any) {
    control.setValidators(validators);
  }

  public clearValidators(control: AbstractControl) {
    control.clearValidators();
  }

  public setAsyncValidators(control: AbstractControl, validators: any) {
    control.setAsyncValidators(validators);
  }

  public clearAsyncValidators(control: AbstractControl) {
    control.clearAsyncValidators();
  }
}
